import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "./api";
import { maybeUpsellPlanLimit, PlanLimitError } from "./plan-limit-store";

export type AlertView = "upcoming" | "due" | "past" | "suggested" | "dismissed";
export type AlertStatus = "suggested" | "confirmed" | "dismissed" | "done" | "cancelled";
export type AlertSourceType = "employee_field" | "document" | "incident" | "manual";
export type AlertRecurrence = "none" | "monthly" | "quarterly" | "semiannual" | "annual" | "custom";
export type AlertRecurrenceUnit = "day" | "week" | "month" | "year";
export type FollowUpRecipient = "workspace" | "employee" | "coordinator" | "manager";
export type FollowUpSchedule = {
  steps: { daysBefore: number; to: FollowUpRecipient[] }[];
  overdue: { everyDays: number; to: FollowUpRecipient[] } | null;
};

export type AlertData = {
  id: string;
  employeeId: string | null;
  employeeName: string | null;
  documentId: string | null;
  sourceType: AlertSourceType;
  sourceRef: string | null;
  category: string;
  categoryLabel: string;
  title: string;
  description: string | null;
  eventDate: string;
  leadDays: number;
  remindAt: string;
  recurrence: AlertRecurrence;
  recurrenceInterval: number | null;
  recurrenceUnit: AlertRecurrenceUnit | null;
  recurrenceEndsOn: string | null;
  recurrenceMaxCount: number | null;
  occurrenceCount: number;
  /** Set when another feature's record owns this follow-up and resolves it. */
  subjectType: string | null;
  subjectId: string | null;
  /** Null for alerts made before schedules: they email once at `remindAt`. */
  schedule: FollowUpSchedule | null;
  /** Reminders paused, e.g. a renewal waiting for HR. */
  held: boolean;
  holdReason: string | null;
  status: AlertStatus;
  closeReason: string | null;
  snoozedUntil: string | null;
  read: boolean;
  /** Read, but its source changed since (a regenerated document) — "Updated". */
  updated: boolean;
  notifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AlertCounts = {
  suggested: number;
  upcoming: number;
  due: number;
  unread: number;
};

export type PatchAction =
  | "accept"
  | "dismiss"
  | "done"
  | "skip"
  | "cancel"
  | "reopen"
  | "read"
  | "unread"
  | "snooze"
  | "unsnooze"
  | "edit";

/** How a reminder repeats. Custom needs interval and unit; at most one end. */
export type RepeatFields = {
  recurrence?: AlertRecurrence;
  recurrenceInterval?: number | null;
  recurrenceUnit?: AlertRecurrenceUnit | null;
  recurrenceEndsOn?: string | null;
  recurrenceMaxCount?: number | null;
};

export type CreateAlertBody = RepeatFields & {
  employeeId?: string | null;
  title: string;
  description?: string | null;
  eventDate: string;
  leadDays?: number;
};

export type PatchAlertBody = RepeatFields & {
  action: PatchAction;
  snoozedUntil?: string | null;
  title?: string;
  description?: string | null;
  eventDate?: string;
  leadDays?: number;
  /** Cancel: why, kept in the reminder's history. */
  reason?: string | null;
  /** Done / Skip: the occurrence being closed, so a repeated click is a no-op. */
  occurrenceDate?: string;
};

/** One closed occurrence of a repeating reminder. */
export type AlertOccurrence = {
  id: string;
  eventDate: string;
  outcome: "done" | "skipped" | "resolved";
  note: string | null;
  actorId: string | null;
  createdAt: string;
};

class AlertApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new AlertApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // Making a reminder repeat counts against the plan's repeating reminders.
    if (maybeUpsellPlanLimit(res.status, data)) {
      throw new PlanLimitError(String((data as { error?: unknown }).error ?? ""));
    }
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new AlertApiError(message, res.status);
  }
  return data as T;
}

/** Alerts for a given tab/view, optionally scoped to one employee and capped. */
export async function listAlerts(
  view: AlertView,
  employeeId?: string,
  limit?: number,
): Promise<AlertData[]> {
  const params = new URLSearchParams({ view });
  if (employeeId) params.set("employeeId", employeeId);
  if (limit) params.set("limit", String(limit));
  return request<AlertData[]>(`/api/alerts?${params.toString()}`);
}

/** Filters + paging for the alerts list (server-side). */
export type AlertsPageParams = {
  view: AlertView;
  limit: number;
  offset: number;
  employeeId?: string;
  q?: string;
  source?: AlertSourceType;
  from?: string;
  to?: string;
  sort?: "reminder" | "employee" | "type" | "date";
  dir?: "asc" | "desc";
};

/** One page of alerts plus the overall total for the active view + filters. */
export type AlertsPage = { items: AlertData[]; total: number };

/** A filtered, paginated slice of a view (`/api/alerts/page`). */
export async function fetchAlertsPage(params: AlertsPageParams): Promise<AlertsPage> {
  const search = new URLSearchParams({
    view: params.view,
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.employeeId) search.set("employeeId", params.employeeId);
  if (params.q) search.set("q", params.q);
  if (params.source) search.set("source", params.source);
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  return request<AlertsPage>(`/api/alerts/page?${search.toString()}`);
}

/** Badge counts for the bell and tab headers. */
export async function getAlertCounts(): Promise<AlertCounts> {
  return request<AlertCounts>("/api/alerts/counts");
}

/** Create a manual reminder (starts confirmed). */
export async function createAlert(body: CreateAlertBody): Promise<AlertData> {
  return request<AlertData>("/api/alerts", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Apply a lifecycle action (accept / dismiss / snooze / read / edit / …). */
export async function patchAlert(id: string, body: PatchAlertBody): Promise<AlertData> {
  return request<AlertData>(`/api/alerts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** A repeating reminder's past occurrences, newest first. */
export async function listAlertOccurrences(id: string): Promise<AlertOccurrence[]> {
  return request<AlertOccurrence[]>(`/api/alerts/${id}/occurrences`);
}

/** Accept or dismiss many suggestions at once. */
export async function bulkAlerts(
  ids: string[],
  action: "accept" | "dismiss",
): Promise<{ updated: number }> {
  return request<{ updated: number }>("/api/alerts/bulk", {
    method: "POST",
    body: JSON.stringify({ ids, action }),
  });
}

/** Marks every alert the bell shows (the "due" view) read. */
export async function markAllAlertsRead(): Promise<{ updated: number }> {
  return request<{ updated: number }>("/api/alerts/read-all", { method: "POST" });
}

/** Permanently delete an alert. */
export async function deleteAlert(id: string): Promise<void> {
  await request<void>(`/api/alerts/${id}`, { method: "DELETE" });
}

export { AlertApiError };
